import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { Equipment, LogHoursRequest } from "@/api/types";
import { localToday } from "@/hooks/useEquipment";
import { DialogAlert, EquipmentDialogShell } from "./EquipmentDialogShell";

/** The API's field names -> the dialog's. */
const FIELD_OF: Record<string, "hours" | "date" | "note"> = { hours: "hours", readon: "date", note: "note" };

/**
 * ef-061-8 — "Log hours for {name}": Hours (the meter reading), Date (defaults to today, never in the
 * future), Note. A manual reading — nothing here guesses. A reading that disagrees with its date-neighbours
 * needs a note: the server answers 400 on `note`, which is shown inline (one alert, `aria-invalid` on Note)
 * and the dialog STAYS OPEN so the person can add one.
 */
export function LogHoursDialog({
  equipment,
  onClose,
  onSaved,
}: {
  equipment: Equipment;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = localToday();
  const [hours, setHours] = useState("");
  const [readOn, setReadOn] = useState(today);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());
  const hoursId = useId();
  const dateId = useId();
  const noteId = useId();
  const inFlight = useRef(false);

  const flag = (f: string): true | undefined => (invalid.has(f) ? true : undefined);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return; // a double-click must not send a second request
    setError(null);
    setInvalid(new Set());

    const figure = hours.trim() === "" ? NaN : Number(hours);
    if (!Number.isFinite(figure) || figure < 0) {
      setInvalid(new Set(["hours"]));
      setError("Hours: enter the meter reading, like 1250.5.");
      return;
    }
    if (readOn === "") {
      setInvalid(new Set(["date"]));
      setError("Date: pick the day of the reading.");
      return;
    }

    const body: LogHoursRequest = { hours: figure, readOn };
    if (note.trim() !== "") body.note = note.trim();

    inFlight.current = true;
    setSaving(true);
    try {
      await demoApi.logEquipmentHours(equipment.id, body);
      onSaved();
      onClose();
    } catch (err) {
      const fields = Object.keys(problemFieldErrors(err) ?? {})
        .map((k) => FIELD_OF[k.toLowerCase()])
        .filter((f): f is "hours" | "date" | "note" => f !== undefined);
      setInvalid(new Set(fields));
      setError(problemMessage(err, "Could not save this reading."));
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  const formId = useId();

  return (
    <EquipmentDialogShell
      title={`Log hours for ${equipment.name}`}
      saving={saving}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save reading"}
          </button>
        </>
      }
    >
      {({ alertId }) => (
        <form id={formId} className="emp-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div className="equipment-dialog__row">
            <div className="emp-dialog__field">
              <label htmlFor={hoursId}>Hours</label>
              <input
                id={hoursId}
                type="number"
                inputMode="decimal"
                step="any"
                min={0}
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                aria-invalid={flag("hours")}
                aria-describedby={invalid.has("hours") ? alertId : undefined}
              />
            </div>
            <div className="emp-dialog__field">
              <label htmlFor={dateId}>Date</label>
              <input
                id={dateId}
                type="date"
                max={today}
                value={readOn}
                onChange={(e) => setReadOn(e.target.value)}
                aria-invalid={flag("date")}
                aria-describedby={invalid.has("date") ? alertId : undefined}
              />
            </div>
          </div>
          <div className="emp-dialog__field">
            <label htmlFor={noteId}>Note</label>
            <input
              id={noteId}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoComplete="off"
              aria-invalid={flag("note")}
              aria-describedby={invalid.has("note") ? alertId : undefined}
            />
          </div>

          <DialogAlert id={alertId} message={error} />
        </form>
      )}
    </EquipmentDialogShell>
  );
}
